import type {
  ChatAnswer,
  ChatEvent,
  ChatMessageView,
  ChatScope,
  ChatSessionView,
  ChatStep,
  CompareRoute,
  EntitySummary,
} from '@hoopoe/shared';
import { CHAT_QUESTION_MAX, ROUTE_LABELS } from '@hoopoe/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Loader2, MessagesSquare, Plus, Send } from 'lucide-react';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { AnswerView } from '../components/chat/answer';
import { CompareAnswers } from '../components/chat/compare-answers';
import { DataText } from '../components/data/data-text';
import { ConnectionLost } from '../components/guidance/connection-lost';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { ApiError, api } from '../lib/api';
import { askQuestion, ChatLostError, checkChatAnswer, useAdoptAnswer, useChatSession, useChatSessions, useStartChat } from '../lib/chat';
import { cn } from '../lib/cn';
import { collectionTree, useCollections } from '../lib/library';

// Chat (PLAN §8, §12.7 screen 9; task 28): questions answered from the Library and the graph, every statement with
// its sources, caveats first, answered live as the server streams its progress. A conversation can be limited to a
// collection or a Case. In Compare mode two or three routes answer side by side and the person keeps one.

export interface ChatSearch {
  session?: string;
  collection?: string;
  case?: string;
}
const str = (v: unknown) => (typeof v === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(v) ? v : undefined);

export const Route = createFileRoute('/chat')({
  validateSearch: (s: Record<string, unknown>): ChatSearch => ({
    session: str(s.session),
    collection: str(s.collection),
    case: str(s.case),
  }),
  component: ChatScreen,
});

const TOUR = {
  id: 'chat',
  steps: [
    {
      target: '[data-tour="chat-scope"]',
      title: 'What the chat answers from',
      body: 'Everything in the Library and the graph, or only one collection (for example the interviews) or one Case.',
      learnMore: 'ask-the-chat',
    },
    {
      target: '[data-tour="chat-ask"]',
      title: 'Ask in any language',
      body: 'Each statement in the answer carries numbered sources. A statement without one is marked: don’t rely on it.',
    },
  ],
};

interface Pending {
  question: string;
  status: string;
  steps: { route: CompareRoute | null; step: ChatStep }[];
  answers: ChatAnswer[];
  compare: boolean;
}

function ChatScreen() {
  useScreenTour(TOUR);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/chat' });
  const sessions = useChatSessions();
  const current = useChatSession(search.session);
  const qc = useQueryClient();
  const start = useStartChat();
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** An answer whose stream was lost; it may still be finishing (D413). */
  const [lost, setLost] = useState<ChatLostError | null>(null);
  const initialScope: ChatScope = search.collection
    ? { kind: 'collection', id: search.collection }
    : search.case
      ? { kind: 'case', id: search.case }
      : { kind: 'all', id: null };
  const [scope, setScope] = useState<ChatScope>(initialScope);
  const bottom = useRef<HTMLDivElement>(null);

  // "Ask the chat" from a collection or a Case while the chat is open: the new conversation takes that scope.
  // biome-ignore lint/correctness/useExhaustiveDependencies: also reset when another conversation opens
  useEffect(() => {
    setScope(
      search.collection
        ? { kind: 'collection', id: search.collection }
        : search.case
          ? { kind: 'case', id: search.case }
          : { kind: 'all', id: null },
    );
    setError(null);
    setLost(null);
  }, [search.collection, search.case, search.session]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when the conversation grows
  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: 'end' });
  }, [current.data?.messages.length, pending?.answers.length]);

  async function ask(question: string) {
    setError(null);
    setLost(null);
    let sessionId = search.session;
    try {
      if (!sessionId) {
        sessionId = (await start.mutateAsync(scope)).session.id;
        await navigate({ search: { session: sessionId }, replace: true });
      }
      setPending({ question, status: 'Sending…', steps: [], answers: [], compare: false });
      const id = sessionId;
      await askQuestion(id, question, (e: ChatEvent) => {
        if (e.type === 'error') setError(e.message);
        setPending(p => {
          if (!p) return p;
          if (e.type === 'status') return { ...p, status: e.text };
          if (e.type === 'routes') return { ...p, compare: e.compare };
          if (e.type === 'step') return { ...p, steps: [...p.steps, { route: e.route, step: e.step }] };
          if (e.type === 'answer') return { ...p, answers: [...p.answers, e.answer] };
          return p;
        });
        if (e.type === 'done') {
          qc.setQueryData(['chat', 'session', id], (old: { session: ChatSessionView; messages: ChatMessageView[] } | undefined) =>
            old ? { session: e.session, messages: old.messages } : old,
          );
        }
      });
      await qc.invalidateQueries({ queryKey: ['chat', 'session', id] });
      await qc.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    } catch (e) {
      if (e instanceof ChatLostError) {
        setLost(e);
        // The question is saved: show it while the answer may still be finishing.
        await qc.invalidateQueries({ queryKey: ['chat', 'session', e.sessionId] });
      } else setError(e instanceof ApiError ? e.message : 'The question couldn’t be sent. Try again.');
    } finally {
      setPending(null);
    }
  }

  /** Check again: an answer saved after all shows up in the conversation. */
  const checkLost = async () => {
    if (!lost || !(await checkChatAnswer(lost.sessionId, lost.since))) return false;
    setLost(null);
    await qc.invalidateQueries({ queryKey: ['chat', 'session', lost.sessionId] });
    await qc.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    return true;
  };

  const session = current.data?.session;
  return (
    <div className="space-y-6">
      <PageHeader
        title="Chat"
        description="Ask questions about the Library and the graph. Every answer shows its sources; it never changes any data."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/chat" search={{}}>
              <Plus aria-hidden="true" /> New conversation
            </Link>
          </Button>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <nav aria-label="Your conversations" className="space-y-2">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Your conversations</h2>
          {sessions.data?.sessions.length === 0 && <p className="text-sm text-muted-foreground">None yet.</p>}
          <ul className="space-y-1">
            {sessions.data?.sessions.map(s => (
              <li key={s.id}>
                <Link
                  to="/chat"
                  search={{ session: s.id }}
                  aria-current={s.id === search.session ? 'page' : undefined}
                  className={cn('block rounded-md px-2 py-1.5 text-sm hover:bg-muted', s.id === search.session && 'bg-muted font-medium')}
                >
                  <DataText className="line-clamp-2">{s.title}</DataText>
                  {s.scope.kind !== 'all' && (
                    <span className="block text-xs text-muted-foreground">
                      {s.scope.kind === 'collection' ? 'Collection' : 'Case'}: <DataText>{s.scope.label}</DataText>
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <section aria-label="Conversation" className="min-w-0 space-y-4">
          {search.session && current.isError ? (
            <ErrorState
              error={current.error}
              title="This conversation couldn’t be opened"
              onRetry={() => current.refetch()}
              learnMore="ask-the-chat"
            />
          ) : (
            <>
              {session ? (
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold">
                    <DataText>{session.title}</DataText>
                  </h2>
                  <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                    {session.scope.kind === 'all'
                      ? 'Answers from everything'
                      : session.scope.kind === 'collection'
                        ? 'Collection: '
                        : 'Case: '}
                    {session.scope.kind !== 'all' && <DataText>{session.scope.label}</DataText>}
                  </span>
                </div>
              ) : (
                !search.session && <ScopePicker scope={scope} onChange={setScope} />
              )}
              <Tip id="chat-sources" learnMore="ask-the-chat">
                Numbers after a statement are its sources: open them to read the passage or the claim. Answers say when they rest on
                interviews, unreviewed or disputed data.
              </Tip>

              {current.data?.messages.length === 0 && !pending && (
                <EmptyState icon={MessagesSquare} title="Ask your first question" learnMore="ask-the-chat">
                  For example: “Who funds the Meridian Policy Forum?” or “What did interviewees say about the 2024 summit?”
                </EmptyState>
              )}
              {!search.session && !pending && (
                <EmptyState icon={MessagesSquare} title="Ask a question about your material" learnMore="ask-the-chat">
                  The chat searches every Library document, whatever was extracted from it, and the graph. It can’t change anything.
                </EmptyState>
              )}

              <ol className="space-y-6">
                {current.data?.messages.map(m => (
                  <li key={m.id}>
                    <Message m={m} sessionId={search.session ?? ''} />
                  </li>
                ))}
                {pending && (
                  <li>
                    <PendingView p={pending} />
                  </li>
                )}
              </ol>
              {error && (
                <p role="alert" className="text-sm text-danger-foreground">
                  {error}
                </p>
              )}
              {lost && <ConnectionLost message={lost.message} onCheck={checkLost} learnMore="ask-the-chat" />}
              <div ref={bottom} />
              <Composer busy={!!pending || start.isPending} onAsk={ask} />
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function ScopePicker({ scope, onChange }: { scope: ChatScope; onChange: (s: ChatScope) => void }) {
  const id = useId();
  const collections = useCollections();
  const cases = useQuery({
    queryKey: ['entities', 'cases'],
    queryFn: () => api<{ entities: EntitySummary[] }>('/entities?kind=case&limit=100'),
  });
  const value = scope.kind === 'all' ? 'all' : `${scope.kind}:${scope.id}`;
  return (
    <div className="space-y-1.5" data-tour="chat-scope">
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="text-sm font-medium">
          Answer from
        </label>
        <FieldHelp
          label="Answer from"
          help="Everything: the whole Library and the graph. A collection: only its documents (and its sub-collections), following its reading guide. A Case: the Case’s entities and their documents."
          learnMore="ask-the-chat"
        />
      </div>
      <select
        id={id}
        value={value}
        onChange={e => {
          const [kind, ref] = e.target.value.split(':');
          onChange(kind === 'collection' || kind === 'case' ? { kind, id: ref ?? null } : { kind: 'all', id: null });
        }}
        className="h-9 w-full max-w-md rounded-md border border-input bg-surface px-2 text-sm"
      >
        <option value="all">Everything: the Library and the graph</option>
        {(collections.data?.collections.length ?? 0) > 0 && (
          <optgroup label="A collection">
            {collectionTree(collections.data?.collections ?? []).map(({ c, depth }) => (
              <option key={c.id} value={`collection:${c.id}`}>
                {`${'  '.repeat(depth)}${c.name}`}
              </option>
            ))}
          </optgroup>
        )}
        {(cases.data?.entities.length ?? 0) > 0 && (
          <optgroup label="A Case">
            {cases.data?.entities.map(c => (
              <option key={c.id} value={`case:${c.id}`}>
                {c.displayName}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </div>
  );
}

function Composer({ busy, onAsk }: { busy: boolean; onAsk: (q: string) => Promise<void> }) {
  const id = useId();
  const [q, setQ] = useState('');
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    const question = q.trim();
    if (!question || busy) return;
    setQ('');
    await onAsk(question);
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-lg border bg-surface p-3" data-tour="chat-ask">
      <label htmlFor={id} className="text-sm font-medium">
        Your question
      </label>
      <textarea
        id={id}
        value={q}
        maxLength={CHAT_QUESTION_MAX}
        rows={3}
        dir="auto"
        onChange={e => setQ(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void submit();
          }
        }}
        placeholder="Ask about people, organisations, events, money flows or what a document says…"
        className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
      />
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Enter to send, Shift+Enter for a new line.</p>
        <Button type="submit" disabled={busy || !q.trim()}>
          {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Send aria-hidden="true" />}
          Ask
        </Button>
      </div>
    </form>
  );
}

function Question({ text }: { text: string }) {
  return (
    <div className="ms-auto max-w-[80%] rounded-lg bg-muted px-3 py-2 text-sm">
      <span className="sr-only">You asked: </span>
      <DataText className="whitespace-pre-line">{text}</DataText>
    </div>
  );
}

function Message({ m, sessionId }: { m: ChatMessageView; sessionId: string }) {
  if (m.role === 'user') return <Question text={m.content} />;
  if (m.status === 'choose' && m.answers) return <CompareAnswersFor m={m} sessionId={sessionId} />;
  return (
    <article aria-label="Answer" className="space-y-2">
      {m.answer ? <AnswerView answer={m.answer} anchor={m.id} /> : <p className="text-sm text-danger-foreground">No answer.</p>}
      {m.answers && m.adoptedRoute && (
        <p className="text-xs text-muted-foreground">
          You kept {ROUTE_LABELS[m.adoptedRoute]}’s answer; compared with{' '}
          {m.answers
            .filter(a => a.route !== m.adoptedRoute)
            .map(a => a.label)
            .join(' and ')}
          .
        </p>
      )}
    </article>
  );
}

function CompareAnswersFor({ m, sessionId }: { m: ChatMessageView; sessionId: string }) {
  const adopt = useAdoptAnswer(sessionId);
  return (
    <CompareAnswers
      m={m}
      pending={adopt.isPending}
      error={adopt.isError ? (adopt.error instanceof ApiError ? adopt.error.message : 'The answer couldn’t be kept.') : null}
      onKeep={route => adopt.mutate({ messageId: m.id, route })}
    />
  );
}

function PendingView({ p }: { p: Pending }) {
  return (
    <div className="space-y-3">
      <Question text={p.question} />
      <div role="status" aria-live="polite" className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        {p.status}
      </div>
      {p.steps.length > 0 && (
        <ul className="list-disc space-y-0.5 ps-5 text-xs text-muted-foreground">
          {p.steps.map((s, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: steps only grow
            <li key={i}>
              {s.route && p.compare ? `${ROUTE_LABELS[s.route]}: ` : ''}
              <DataText>{s.step.summary}</DataText>
            </li>
          ))}
        </ul>
      )}
      {p.answers.length > 0 && (
        <div className={cn('grid gap-4', p.compare && 'md:grid-cols-2')}>
          {p.answers.map(a => (
            <div key={a.route} className="rounded-lg border bg-surface p-3">
              {p.compare && <h3 className="mb-2 font-semibold">{a.label}</h3>}
              <AnswerView answer={a} anchor={`pending-${a.route}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
